// DocumentSelectorModal.js
import React, { useState, useEffect } from 'react';
import { Modal, Button, Spin, message, Empty, Pagination, Tabs, Input } from 'antd';
import {
  FilePdfOutlined,
  FileWordOutlined,
  FilePptOutlined,
  FileTextOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { api } from '../../../../../../services';
import './DocumentSelectorModal.scss';
import { getToken } from '../../../../../../services/api/user'
import { config } from '../../../../../../constants'

const { TabPane } = Tabs;
const { Search } = Input;

const DocumentSelectorModal = ({ visible, onClose, onSelect, activeOrg }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(12); // 12 items per page (3 rows × 4 columns)
  const [total, setTotal] = useState(0);
  const [activeTab, setActiveTab] = useState('my_media');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [searchQuery, setSearchQuery] = useState('');
  const [isRecommendationLoading, setIsRecommendationLoading] = useState(false);
  const [shlToken, setShlToken] = useState(null);

  // Accepted file formats
  const ACCEPTED_FORMATS = ['docx', 'doc', 'pdf', 'pptx', 'ppt', 'txt'];

  useEffect(() => {
    if (visible) {
      fetchDocuments(1, activeTab, searchQuery);
    } else {
      // Reset when modal closes
      setCurrentPage(1);
      setSelectedDocs([]);
      setSearchQuery('');
      setIsRecommendationLoading(false);
    }
  }, [visible]);

  useEffect(() => {
    const fetchToken = async () => {
      try {
        const token = await getToken();
        setShlToken(token);
      } catch (error) {
        console.error('Error fetching token:', error);
      }
    };

    fetchToken();
  }, []);

  const getFileExtension = (filename) => {
    if (!filename) return 'file';
    const parts = filename.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : 'file';
  };

  const formatFileType = (mimeType, filename) => {
    if (!mimeType) return getFileExtension(filename);

    if (mimeType.includes('pdf')) return 'pdf';
    if (mimeType.includes('word') || mimeType.includes('msword')) return 'docx';
    if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return 'pptx';
    if (mimeType.includes('text/plain')) return 'txt';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'xlsx';

    return getFileExtension(filename);
  };

  const isAcceptedFormat = (fileType) => {
    return ACCEPTED_FORMATS.includes(fileType.toLowerCase());
  };

  const trimFileName = (filename) => {
    if (!filename) return '';

    const maxLength = 25;
    const parts = filename.split('.');
    const extension = parts.length > 1 ? parts.pop() : '';
    const nameWithoutExt = parts.join('.');

    if (filename.length <= maxLength) {
      return filename;
    }

    const trimmedName = nameWithoutExt.substring(0, maxLength - extension.length - 3);
    return `${trimmedName}...${extension}`;
  };

  const fetchDocuments = async (page, category, search = '') => {
    setLoading(true);
    try {
      const offset = (page - 1) * pageSize;

      if (category === 'my_media') {
        // Fetch My Media data from API
        const response = await api.library.getLibraryList({
          limit: 15,
          onlyMedia: true,
          offset: 0,
          search: search,
          sort: 'NEWEST',
          type: '',
          isActiveOrg: true
        });

        console.log("response from library ", response)

        // Transform API response and filter by accepted formats and is_ai_enabled
        const transformedDocs = response.data.rows
          .filter(item => item.is_ai_enabled === true)
          .map(item => ({
            id: item.id,
            name: item.filename,
            type: formatFileType(item.media_mime_type, item.filename),
            category: 'my_media',
            owner: item.creator ? item.creator.name : 'Unknown',
            lastUpdated: item.updated_at,
            link: item.link,
            mimeType: item.media_mime_type
          }))
          .filter(doc => isAcceptedFormat(doc.type));

        setDocuments(transformedDocs);
        setTotal(transformedDocs.length);
      } else if (category === 'org_media') {
        // Fetch Organization Library (SOP) data from API
        const response = await api.library.getLibraryList({
          limit: 15,
          onlyMedia: true,
          offset: 0,
          search: search,
          sort: 'NEWEST',
          type: '',
          isOrg: true
        });

        // Transform API response and filter by accepted formats and is_ai_enabled
        const transformedDocs = response.data.rows
          .filter(item => item.is_ai_enabled === true)
          .map(item => ({
            id: item.id,
            name: item.filename,
            type: formatFileType(item.media_mime_type, item.filename),
            category: 'org_media',
            owner: item.creator ? item.creator.name : 'Unknown',
            lastUpdated: item.updated_at,
            link: item.link,
            mimeType: item.media_mime_type
          }))
          .filter(doc => isAcceptedFormat(doc.type));

        setDocuments(transformedDocs);
        setTotal(transformedDocs.length);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      message.error('Failed to load documents');
      setDocuments([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const getFileIcon = (type) => {
    const iconStyle = { fontSize: '36px' };
    switch (type.toLowerCase()) {
      case 'pdf':
        return <FilePdfOutlined style={{ ...iconStyle, color: '#ff4d4f' }} />;
      case 'doc':
      case 'docx':
        return <FileWordOutlined style={{ ...iconStyle, color: '#1890ff' }} />;
      case 'ppt':
      case 'pptx':
        return <FilePptOutlined style={{ ...iconStyle, color: '#ff7a45' }} />;
      case 'txt':
        return <FileTextOutlined style={{ ...iconStyle, color: '#52c41a' }} />;
      default:
        return <FileTextOutlined style={{ ...iconStyle, color: '#8c8c8c' }} />;
    }
  };

  const handleDocumentClick = (doc) => {
    const isSelected = selectedDocs.some(d => d.id === doc.id);

    if (isSelected) {
      setSelectedDocs(selectedDocs.filter(d => d.id !== doc.id));
    } else {
      setSelectedDocs([...selectedDocs, doc]);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchDocuments(page, activeTab, searchQuery);
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    setCurrentPage(1);
    setSearchQuery('');
    fetchDocuments(1, key, '');
  };

  const handleSearch = async (value) => {
    setSearchQuery(value);
    setCurrentPage(1);
    console.log("log value param", value)
    console.log("log documents", documents)

    // Make API call for filename recommendations
    if (value.length > 0 && !isRecommendationLoading) {
      setIsRecommendationLoading(true);
      try {
        const response = await axios.post(
          `${config.aiContentGenerationUrl}recommend-filenames`,
          {
            query: value,
            org_id: String(activeOrg || '96'),
          },
          {
            headers: {
              'Authorization': `Bearer ${shlToken}`,
              'Content-Type': 'application/json'
            }
          }
        );

        console.log('Filename recommendations response:', response.data);

        // Extract suggested filenames and filter documents
        if (response.data.success && response.data.suggested_files && response.data.suggested_files.length > 0) {
          const suggestedFiles = response.data.suggested_files;

          // Filter documents that match the suggested files
          const matchedDocuments = documents.filter(doc => {
            if (!doc.link) return false;

            // Extract the path from the document link (before the query parameters)
            const linkPath = doc.link.split('?')[0];
            const extractedPath = linkPath.substring(linkPath.indexOf('users/'));

            // Check if this path matches any of the suggested files
            return suggestedFiles.some(suggestedFile => suggestedFile === extractedPath);
          });

          console.log('Matched documents:', matchedDocuments);

          // Update documents state with matched results
          if (matchedDocuments.length > 0) {
            setDocuments(matchedDocuments);
            setTotal(matchedDocuments.length);
          } else {
            // If no matches, fetch normally
            fetchDocuments(1, activeTab, value);
          }
        } else {
          // If no suggestions, fetch normally
          fetchDocuments(1, activeTab, value);
        }
      } catch (error) {
        console.error('Error fetching filename recommendations:', error);
        setIsRecommendationLoading(false);
        // On error, fetch normally
        fetchDocuments(1, activeTab, value);
      } finally {
        setIsRecommendationLoading(false);
      }
    } else {
      // If search query is empty or already loading, fetch normally
      fetchDocuments(1, activeTab, value);
    }
  };

  const handleSelect = () => {
    if (selectedDocs.length > 0) {
      onSelect(selectedDocs);
      handleClose();
    } else {
      message.warning('Please select at least one document');
    }
  };

  const handleClose = () => {
    setSelectedDocs([]);
    setCurrentPage(1);
    setSearchQuery('');
    onClose();
  };

  const isDocSelected = (docId) => {
    return selectedDocs.some(d => d.id === docId);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return date.toLocaleDateString();
  };

  const renderGridView = () => (
    <div className="documents-grid">
      {documents.map((doc) => (
        <div
          key={doc.id}
          className={`document-tile ${isDocSelected(doc.id) ? 'selected' : ''}`}
          onClick={() => handleDocumentClick(doc)}
        >
          {isDocSelected(doc.id) && (
            <div className="selected-badge">
              <CheckCircleOutlined />
            </div>
          )}
          <div className="document-icon">{getFileIcon(doc.type)}</div>
          <div className="document-name" title={doc.name}>
            {trimFileName(doc.name)}
          </div>
          <div className="document-meta">
            <div className="document-owner" title={doc.owner}>{doc.owner}</div>
            <div className="document-date">{formatDate(doc.lastUpdated)}</div>
          </div>
          <div className="document-type">{doc.type.toUpperCase()}</div>
        </div>
      ))}
    </div>
  );

  const renderListView = () => (
    <div className="documents-list">
      {documents.map((doc) => (
        <div
          key={doc.id}
          className={`document-list-item ${isDocSelected(doc.id) ? 'selected' : ''}`}
          onClick={() => handleDocumentClick(doc)}
        >
          <div className="list-item-icon">{getFileIcon(doc.type)}</div>
          <div className="list-item-details">
            <div className="list-item-name" title={doc.name}>{doc.name}</div>
            <div className="list-item-meta">
              <span className="list-item-owner">{doc.owner}</span>
              <span className="list-item-separator">•</span>
              <span className="list-item-date">{formatDate(doc.lastUpdated)}</span>
              <span className="list-item-separator">•</span>
              <span className="list-item-type">{doc.type.toUpperCase()}</span>
            </div>
          </div>
          {isDocSelected(doc.id) && (
            <CheckCircleOutlined className="list-item-check" style={{ fontSize: '20px', color: '#1890ff' }} />
          )}
        </div>
      ))}
    </div>
  );

  return (
    <Modal
      visible={visible}
      title={
        <div>
          Select Document(s)
          {selectedDocs.length > 0 && (
            <span style={{ marginLeft: '10px', color: '#1890ff', fontSize: '14px' }}>
              ({selectedDocs.length} selected)
            </span>
          )}
        </div>
      }
      onCancel={handleClose}
      width={1000}
      footer={[
        <Button key="close" onClick={handleClose}>
          Close
        </Button>,
        <Button
          key="select"
          type="primary"
          onClick={handleSelect}
          disabled={selectedDocs.length === 0}
        >
          Select ({selectedDocs.length})
        </Button>
      ]}
      className="document-selector-modal"
    >
      <div className="document-selector-content">
        <div className="controls-bar">
          <Search
            placeholder="Search documents..."
            allowClear
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onSearch={handleSearch}
            style={{ width: 300, marginRight: 'auto' }}
          />
          <div className="view-toggle">
            <Button
              type={viewMode === 'grid' ? 'primary' : 'default'}
              icon="appstore"
              onClick={() => setViewMode('grid')}
              title="Grid View"
            />
            <Button
              type={viewMode === 'list' ? 'primary' : 'default'}
              icon="unordered-list"
              onClick={() => setViewMode('list')}
              style={{ marginLeft: '8px' }}
              title="List View"
            />
          </div>
        </div>

        <Tabs activeKey={activeTab} onChange={handleTabChange}>
          <TabPane tab="My Media" key="my_media">
            {loading ? (
              <div className="loading-container">
                <Spin size="large" tip="Loading documents..." />
              </div>
            ) : documents.length === 0 ? (
              <Empty description="No documents available" />
            ) : (
              <>
                {viewMode === 'grid' ? renderGridView() : renderListView()}

                {total > pageSize && (
                  <div className="pagination-container">
                    <Pagination
                      current={currentPage}
                      pageSize={pageSize}
                      total={total}
                      onChange={handlePageChange}
                      showSizeChanger={false}
                      showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} documents`}
                    />
                  </div>
                )}
              </>
            )}
          </TabPane>

          <TabPane tab="Organization Library (SOP)" key="org_media">
            {loading || isRecommendationLoading ? (
              <div className="loading-container">
                <Spin size="large" tip="Loading documents..." />
              </div>
            ) : documents.length === 0 ? (
              <Empty description="No documents available" />
            ) : (
              <>
                {viewMode === 'grid' ? renderGridView() : renderListView()}

                {total > pageSize && (
                  <div className="pagination-container">
                    <Pagination
                      current={currentPage}
                      pageSize={pageSize}
                      total={total}
                      onChange={handlePageChange}
                      showSizeChanger={false}
                      showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} documents`}
                    />
                  </div>
                )}
              </>
            )}
          </TabPane>
        </Tabs>
      </div>
    </Modal>
  );
};

export default DocumentSelectorModal;